import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { movementActionLabel, formatMovementDelta, resolveLogValidUntil, formatMovementValidUntilCaption, formatMovementValidUntilShort, movementProgram } from '@/features/leads/model/lead-logs';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Banknote,
  Calendar,
  Check,
  ChevronRight,
  Copy,
  Download,
  Gift,
  Lock,
  Mail,
  MessageCircle,
  Package,
  Receipt,
  Search,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Store,
  Trash2,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { canAccessAudit, showLoyaltyCampaigns, showMemberGetMember } from '@/shared/lib/navigation';
import { LeadMgmTab } from '@/features/member-get-member';
import { LeadLoyaltyTab } from '@/features/habibs-loyalty';
import { usePassbook } from '@/shared/lib/passbook';
import { currency, decimal, formatDate, formatTime, whatsappLink } from '@/shared/lib/format';
import {
  maskBirthday,
  maskBirthdayFromStored,
  maskCpf,
  maskPhone,
  maskPhoneFromStored,
} from '@/shared/lib/input-masks';
import {
  formatUniqueIdForDisplay,
  isCpfUniqueIdLabel,
} from '@/shared/lib/unique-id-lookup';
import { computeLeadStats } from '@/features/leads/model/lead-stats';
import { cn } from '@/shared/lib/cn';
import { isCardInstalled, isMultiStoreLeads, passbookCurrency, quantityLabel } from '@/shared/lib/passbook-utils';
import type { Lead, Passbook, PointLog, PointLogProduct } from '@/shared/types';
import { canResendCard, formatCooldownRemaining } from '@/features/leads/model/card-resend';
import { exportMovementCsv } from '@/features/leads/model/export-movement';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { KpiCard } from '@/shared/components/kpi-card';
import { InstallStatusBadge, WalletPlatformBadge } from '@/shared/components/wallet-platform-badge';
import { SortSelect } from '@/shared/components/sort-select';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import { deleteLead, fetchLead, resendCard, saveLead } from '../api/use-leads';
import { setLeadBlocked } from '../api/use-audit';
import { useStores } from '../api/use-stores';
import { inviteCardUrl, inviteWhatsappMessage } from './lead-card';
import { LeadObservationsPanel } from './lead-observations-panel';
import { LeadMessagesTab } from './lead-messages-tab';
import { MaskedInput } from '@/shared/components/masked-input';
import { CopyableField } from '@/shared/components/copyable-field';

const schema = z.object({
  name: z.string().optional(),
  uniqueId: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  birthday: z.string().optional(),
  gender: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

/** Contrato Habib's manda 1/2/3. O select do painel só conhece M/F. */
function genderForForm(value: string | null | undefined): string {
  if (!value) return '';
  const key = value.trim().toLowerCase();
  if (key === 'm' || key === '1' || key === 'masculino') return 'M';
  if (key === 'f' || key === '2' || key === 'feminino') return 'F';
  return value;
}

function genderLabel(value: string | null | undefined): string | null {
  const code = genderForForm(value);
  if (!code) return null;
  if (code === 'M') return 'Masculino';
  if (code === 'F') return 'Feminino';
  return code;
}

type Tab = 'info' | 'movimentos' | 'campanhas' | 'convite' | 'mensagens' | 'indicacoes' | 'excluir';

const LEAD_TABS: Tab[] = ['info', 'movimentos', 'campanhas', 'convite', 'mensagens', 'indicacoes', 'excluir'];

function parseLeadTab(raw: string | null): Tab {
  if (raw && (LEAD_TABS as string[]).includes(raw)) return raw as Tab;
  return 'info';
}

const TABS: Array<{ value: Tab; label: string }> = [
  { value: 'info', label: 'Informações' },
  { value: 'movimentos', label: 'Movimentações' },
  { value: 'campanhas', label: 'Campanhas' },
  { value: 'convite', label: 'Enviar Convite' },
  { value: 'mensagens', label: 'Mensagens' },
  { value: 'indicacoes', label: 'Indicações' },
  { value: 'excluir', label: 'Excluir' },
];

function productName(p: PointLogProduct): string {
  return p.nome ?? p.name ?? '—';
}
function productSku(p: PointLogProduct): string {
  return p.sku ?? '—';
}
function productQty(p: PointLogProduct): number {
  return p.qtde ?? p.qty ?? p.count ?? 1;
}
function productSeller(p: PointLogProduct): string | undefined {
  return p.vendedor ?? p.seller;
}

const MOV_SORT_OPTIONS = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigas' },
  { value: 'value:desc', label: 'Maior valor' },
  { value: 'value:asc', label: 'Menor valor' },
];

function shortCode(id?: string): string {
  if (!id) return '—';
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function MovementCard({
  log,
  index,
  passbook,
  onClick,
}: {
  log: PointLog;
  index: number;
  passbook: Passbook;
  onClick: () => void;
}) {
  const positive = log.value >= 0;
  const itemCount = log.products?.length ?? 0;
  const validUntil = resolveLogValidUntil(log, passbook);
  const historyLine = log.transactionHistory?.split('\n').find((l) => l.trim())?.trim();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.025, 0.3), type: 'spring', stiffness: 240, damping: 26 }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="glass flex cursor-pointer items-center gap-3.5 rounded-2xl p-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glass-sm"
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
        <ShoppingBag className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{movementActionLabel(log, passbook)}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span>{formatDate(log.date)} {formatTime(log.date)}</span>
          {historyLine ? (
            <span className="hidden min-w-0 max-w-[min(100%,14rem)] truncate sm:inline" title={historyLine}>· {historyLine.replace(/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2}\s*-\s*/, '')}</span>
          ) : null}
          {validUntil ? (
            <span className="inline-flex items-center gap-1">· {formatMovementValidUntilShort(new Date(validUntil))}</span>
          ) : null}
          {log.loja ? (
            <span className="inline-flex items-center gap-1">· <Store className="size-3" /> {log.loja}</span>
          ) : null}
          {log.produtoResgate ? (
            <span className="inline-flex items-center gap-1">· <Package className="size-3" /> {log.produtoResgate}</span>
          ) : itemCount > 0 ? (
            <span className="inline-flex items-center gap-1">· <Package className="size-3" /> {itemCount} {itemCount > 1 ? 'itens' : 'item'}</span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end justify-center gap-0.5 text-right">
        <span className={`text-sm font-semibold tabular-nums leading-tight ${positive ? 'text-success' : 'text-destructive'}`}>
          {formatMovementDelta(log, passbook)}
        </span>
        {log.price != null ? (
          <span className="text-xs font-semibold tabular-nums text-foreground/80">
            {currency(log.price, passbookCurrency(passbook))}
          </span>
        ) : null}
        <span className="font-mono text-[10px] leading-tight text-muted-foreground" title={log.externalId ?? log._id}>
          {shortCode(log.externalId ?? log._id)}
        </span>
      </div>
      <ChevronRight className="size-5 shrink-0 text-muted-foreground/50" />
    </motion.div>
  );
}

function MovementDetailSheet({
  log,
  labelQtde,
  passbook,
  onClose,
}: {
  log: PointLog | null;
  labelQtde: string;
  passbook: Passbook;
  onClose: () => void;
}) {
  const [cached, setCached] = useState<PointLog | null>(log);
  useEffect(() => {
    if (log) setCached(log);
  }, [log]);

  const open = Boolean(log);
  const view = log ?? cached;
  const positive = (view?.value ?? 0) >= 0;
  const items = view?.products ?? [];
  const validUntil = view ? resolveLogValidUntil(view, passbook) : undefined;
  const validUntilCaption = validUntil ? formatMovementValidUntilCaption(new Date(validUntil)) : undefined;
  const purchaseCode = view?.externalId ?? view?._id ?? '';
  const [codeCopied, setCodeCopied] = useState(false);

  const copyPurchaseCode = useCallback(async () => {
    if (!purchaseCode) return;
    try {
      await navigator.clipboard.writeText(purchaseCode);
      setCodeCopied(true);
      toast.success('Código copiado!', { duration: 1600 });
      setTimeout(() => setCodeCopied(false), 1600);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  }, [purchaseCode]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-[calc(100%-2rem)] max-w-none flex-col sm:w-1/2 sm:max-w-none">
        <SheetHeader>
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="flex flex-wrap items-center gap-2">
              Detalhe da movimentação
              {view ? (
                <Badge variant={positive ? 'success' : 'secondary'} className="font-normal">{movementActionLabel(view, passbook)}</Badge>
              ) : null}
            </SheetTitle>
            {view ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => exportMovementCsv(view, labelQtde, passbook)}
              >
                <Download className="size-4" /> Exportar
              </Button>
            ) : null}
          </div>
        </SheetHeader>
        {view ? (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-2">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><Calendar className="size-3.5" /> Data</p>
                <p className="mt-2 text-base font-medium">{formatDate(view.date)}</p>
                <p className="text-sm text-muted-foreground">{formatTime(view.date)}</p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{labelQtde}</p>
                <p className={`mt-2 text-base font-semibold tabular-nums ${positive ? 'text-success' : 'text-destructive'}`}>{formatMovementDelta(view, passbook)}</p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Valor da compra</p>
                <p className="mt-2 text-base font-medium tabular-nums">
                  {view.price != null ? currency(view.price, passbookCurrency(passbook)) : '—'}
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><Store className="size-3.5" /> Loja</p>
                <p className="mt-2 truncate text-base font-medium">{view.loja ?? '—'}</p>
              </div>
            </div>

            {validUntilCaption ? (
              <p className="text-center text-xs text-muted-foreground">{validUntilCaption}</p>
            ) : null}

            {view.transactionHistory ? (
              <div className="rounded-xl border border-border/50 bg-[hsl(var(--glass-bg)/0.35)] p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Histórico</p>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/90">{view.transactionHistory.trim()}</p>
              </div>
            ) : null}

            {view.balanceAfter != null && view.balanceAfter > 0 ? (
              <div className="rounded-xl border border-border/50 bg-[hsl(var(--glass-bg)/0.35)] p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Saldo após movimentação</p>
                <p className="mt-2 text-base font-medium tabular-nums">
                  {(() => {
                    const program = movementProgram(passbook);
                    return program === 'cashback' || program === 'balance'
                      ? currency(view.balanceAfter!, passbookCurrency(passbook))
                      : `${decimal(view.balanceAfter!)} ${labelQtde.toLowerCase()}`;
                  })()}
                </p>
              </div>
            ) : null}

            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Código da compra</p>
              {purchaseCode ? (
                <div className="mt-2 flex items-start gap-2">
                  <p className="min-w-0 flex-1 break-all font-mono text-sm leading-relaxed">{purchaseCode}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-10 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label="Copiar código da compra"
                    onClick={() => void copyPurchaseCode()}
                  >
                    {codeCopied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
                  </Button>
                </div>
              ) : (
                <p className="mt-2 font-mono text-sm text-muted-foreground">—</p>
              )}
            </div>

            {view.produtoResgate ? (
              <div className="rounded-xl border border-border/50 bg-[hsl(var(--glass-bg)/0.35)] p-3">
                <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><Package className="size-3" /> Produto resgatado</p>
                <p className="mt-1 text-sm font-medium">{view.produtoResgate}</p>
              </div>
            ) : null}

            {items.length > 0 ? (
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Itens ({items.length})</p>
                <ul className="space-y-2">
                  {items.map((p, j) => {
                    const seller = productSeller(p);
                    return (
                      <li key={j} className="flex items-center gap-3 rounded-lg border border-border/50 bg-[hsl(var(--glass-bg)/0.35)] px-3.5 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{productName(p)}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">
                            SKU {productSku(p)}{seller ? ` · ${seller}` : ''}
                          </p>
                        </div>
                        <Badge variant="secondary" className="shrink-0 font-normal tabular-nums">{productQty(p)}x</Badge>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sem itens detalhados nesta movimentação.</p>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function MovimentacoesTab({ lead, labelQtde, passbook }: { lead: Lead; labelQtde: string; passbook: Passbook }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const [selected, setSelected] = useState<PointLog | null>(null);

  const logs = useMemo(() => lead.logs ?? [], [lead.logs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result = q
      ? logs.filter(
          (l) =>
            (l.loja ?? '').toLowerCase().includes(q) ||
            (l._id ?? '').toLowerCase().includes(q) ||
            (l.externalId ?? '').toLowerCase().includes(q) ||
            (l.transactionHistory ?? '').toLowerCase().includes(q) ||
            (l.produtoResgate ?? '').toLowerCase().includes(q),
        )
      : [...logs];
    result.sort((a, b) => {
      if (sort === 'value:desc') return Math.abs(b.value) - Math.abs(a.value);
      if (sort === 'value:asc') return Math.abs(a.value) - Math.abs(b.value);
      const cmp = (a.date ?? '').localeCompare(b.date ?? '');
      return sort === 'oldest' ? cmp : -cmp;
    });
    return result;
  }, [logs, search, sort]);

  if (logs.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Nenhuma movimentação registrada.</p>
      </Card>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por loja ou código…" className="pl-9" />
        </div>
        <SortSelect value={sort} options={MOV_SORT_OPTIONS} onChange={setSort} />
      </div>

      {visible.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">Nenhuma movimentação encontrada.</p>
        </Card>
      ) : (
        <ul className="space-y-2.5">
          {visible.map((log, i) => (
            <li key={log._id ?? i}>
              <MovementCard log={log} index={i} passbook={passbook} onClick={() => setSelected(log)} />
            </li>
          ))}
        </ul>
      )}

      <MovementDetailSheet
        log={selected}
        labelQtde={labelQtde}
        passbook={passbook}
        onClose={() => setSelected(null)}
      />
    </>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message.trim() ? err.message : fallback;
}

export function LeadDetailPage() {
  const { idPassbook, leadId } = useParams<{ idPassbook: string; leadId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { passbook } = usePassbook();
  const confirm = useConfirm();

  // Usa lead passado via state (navegação da lista) para exibição imediata.
  const preloaded = (location.state as { lead?: Lead } | null)?.lead;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = parseLeadTab(searchParams.get('tab'));
  // Preload sem re-normalizar: `value` da lista pode já ser delta; reprocessar
  // quebra balanceDiff. `fetchLead` normaliza os logs crus do /log/:serial.
  const [lead, setLead] = useState<Lead | null>(preloaded ? { ...preloaded } : null);
  const [isLoading, setIsLoading] = useState(!preloaded);
  const [logsLoading, setLogsLoading] = useState(!preloaded?.serialNumber);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [sending, setSending] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);

  const isInspecting = user?.role === 'Admin' && Boolean(idPassbook);
  // Aba do Indique e Ganhe só com o módulo ligado no passbook.
  const showMgmTab = Boolean(user && passbook && showMemberGetMember(user.role, passbook));
  // Aba de campanhas ativadas no app só nas lojas do motor de fidelidade.
  const showLoyaltyTab = Boolean(user && passbook && showLoyaltyCampaigns(user.role, passbook));
  const visibleTabs = TABS.filter(
    (t) => (t.value !== 'indicacoes' || showMgmTab) && (t.value !== 'campanhas' || showLoyaltyTab),
  );
  // `?tab=indicacoes`/`?tab=campanhas` numa loja sem o módulo cai em Informações (em vez de aba vazia).
  const activeTab: Tab = visibleTabs.some((t) => t.value === tab) ? tab : 'info';

  const handleTabChange = (next: Tab) => {
    setTab(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'info') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  };

  // Sync se a URL mudar (ex.: link do Operador com ?tab=movimentos)
  useEffect(() => {
    const fromUrl = parseLeadTab(searchParams.get('tab'));
    setTab((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  // Ao abrir o detalhe, sobe o scroll (a lista de leads costuma deixar a página no meio).
  // Deep link com ?tab=… mantém o foco nas abas.
  useEffect(() => {
    const tabFromUrl = parseLeadTab(searchParams.get('tab'));
    if (tabFromUrl !== 'info') {
      requestAnimationFrame(() => {
        tabsRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // só ao entrar / trocar de lead — não ao mudar aba
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const labelQtde = quantityLabel(passbook);
  const isCashback = Boolean(passbook.hasCashback);
  const hasMultiStore = isMultiStoreLeads(passbook);
  const { resolveLeadStores } = useStores(idPassbook ?? '', hasMultiStore && Boolean(idPassbook));

  const form = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!idPassbook || !leadId) return;
    // Se já temos o lead do state, ainda assim busca em background para atualizar logs.
    let cancelled = false;
    if (!preloaded) setIsLoading(true);
    setLogsLoading(true);
    fetchLead(idPassbook, leadId, passbook).then((data) => {
      if (cancelled) return;
      if (data) {
        setLead((prev) => ({
          ...data,
          total_points: data.total_points ?? prev?.total_points,
        }));
        form.reset({
          name: data.profile?.name ?? '',
          uniqueId: formatUniqueIdForDisplay(data.profile?.uniqueId, passbook.fieldUniqueIdLabel),
          email: data.email ?? '',
          phone: maskPhoneFromStored(data.phone),
          birthday: maskBirthdayFromStored(data.birthday),
          gender: genderForForm(data.gender),
        });
      }
      setIsLoading(false);
      setLogsLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPassbook, leadId]);

  // Popula form com dados preloaded enquanto API carrega.
  useEffect(() => {
    if (preloaded && !form.formState.isDirty) {
      form.reset({
        name: preloaded.profile?.name ?? '',
        uniqueId: formatUniqueIdForDisplay(preloaded.profile?.uniqueId, passbook.fieldUniqueIdLabel),
        email: preloaded.email ?? '',
        phone: maskPhoneFromStored(preloaded.phone),
        birthday: maskBirthdayFromStored(preloaded.birthday),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloaded]);

  const onSubmitInfo = form.handleSubmit(async (values) => {
    if (!lead || !idPassbook) return;
    if (!lead.serialNumber) {
      toast.error('Lead incompleto — sem serial do cartão.');
      return;
    }
    try {
      await saveLead(idPassbook, {
        _id: lead._id,
        serialNumber: lead.serialNumber,
        profile: { name: lead.profile?.name, uniqueId: lead.profile?.uniqueId },
        email: values.email || undefined,
        phone: values.phone || undefined,
        birthday: lead.birthday || undefined,
        gender: values.gender || undefined,
        observacao: lead.observacao,
        total_points: lead.total_points,
        description: '',
      });
      toast.success('Lead atualizado.');
      setLead((prev) =>
        prev
          ? {
              ...prev,
              email: values.email || undefined,
              phone: values.phone || undefined,
              gender: values.gender || undefined,
            }
          : prev,
      );
    } catch {
      toast.error('Falha ao salvar.');
    }
  });

  const handleResendEmail = async () => {
    if (!lead?.serialNumber || !lead.email || !idPassbook) return;
    const eligibility = canResendCard(lead);
    if (!eligibility.allowed) {
      toast.error(`Aguarde ${formatCooldownRemaining(eligibility.remainingMs)} para reenviar.`);
      return;
    }
    setSending(true);
    try {
      await resendCard(idPassbook, lead.serialNumber, lead.email);
      const refreshed = await fetchLead(idPassbook, lead._id, passbook);
      if (refreshed) {
        setLead(refreshed);
      } else {
        const now = new Date().toISOString();
        setLead((prev) => (prev ? { ...prev, lastCardResendAt: now } : prev));
      }
      toast.success('Cartão reenviado por e-mail.');
    } catch {
      toast.error('Falha ao reenviar.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!lead || !idPassbook) return;
    const ok = await confirm({
      title: 'Excluir cadastro',
      description: `Tem certeza que deseja excluir o cadastro de "${lead.profile?.name}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteLead(idPassbook, lead._id);
      toast.success('Cadastro excluído.');
      navigate(`/loja/${idPassbook}/leads`);
    } catch {
      toast.error('Falha ao excluir.');
    }
  };

  const moneyCode = passbookCurrency(passbook);
  const installed = lead ? isCardInstalled(lead) : false;
  const points = lead
    ? isCashback
      ? currency(lead.total_points ?? 0, moneyCode)
      : String(lead.total_points ?? 0)
    : '0';

  const stats = computeLeadStats(lead?.logs ?? []);
  const { purchaseCount, redemptionCount, lifetimeSpent, yearSpent, avgTicket } = stats;

  const waText = lead ? inviteWhatsappMessage(lead) : undefined;

  const resendEligibility = lead ? canResendCard(lead) : { allowed: true, remainingMs: 0 };
  const storeNames = lead ? resolveLeadStores(lead) : [];

  if (isLoading && !lead) {
    return (
      <>
        <PageHeader
          title="Detalhes do Lead"
          actions={
            <Button variant="secondary" onClick={() => navigate(`/loja/${idPassbook}/leads`)}>
              <ArrowLeft className="size-4" /> Voltar
            </Button>
          }
        />
        <div className="space-y-4">
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-28 rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-10 rounded-xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </>
    );
  }

  if (!lead) {
    return (
      <>
        <PageHeader
          title="Lead não encontrado"
          actions={
            <Button variant="secondary" onClick={() => navigate(`/loja/${idPassbook}/leads`)}>
              <ArrowLeft className="size-4" /> Voltar
            </Button>
          }
        />
        <p className="text-sm text-muted-foreground">
          Não foi possível carregar os dados deste lead.
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Detalhes do Lead"
        description={passbook.organizationName}
        actions={
          <Button variant="secondary" onClick={() => navigate(`/loja/${idPassbook}/leads`)}>
            <ArrowLeft className="size-4" /> Voltar
          </Button>
        }
      />

      {/* Bloqueio */}
      {lead.status === 'blocked' ? (
        <Card className="mb-6 border-destructive/40 bg-destructive/5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 size-5 shrink-0 text-destructive" />
              <div>
                <p className="font-semibold text-destructive">Lead bloqueado</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {lead.blockInfo?.reason || 'Sem motivo registrado.'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {lead.blockInfo?.auto ? 'Bloqueado automaticamente' : 'Bloqueado manualmente'}
                  {lead.blockInfo?.blockedBy ? ` por ${lead.blockInfo.blockedBy}` : ''}
                  {lead.blockInfo?.blockedAt ? ` em ${formatDate(lead.blockInfo.blockedAt)}` : ''}
                </p>
                {lead.blockInfo?.evidence ? (
                  <p className="mt-2 max-w-xl break-words text-xs text-muted-foreground">
                    Evidência: {lead.blockInfo.evidence}
                  </p>
                ) : null}
              </div>
            </div>
            {canAccessAudit(user?.email, passbook) ? <Button
              variant="secondary"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Desbloquear lead',
                  description: 'Ele volta a acumular pontos normalmente.',
                  confirmLabel: 'Desbloquear',
                });
                if (!ok || !idPassbook) return;
                try {
                  await setLeadBlocked(idPassbook, lead._id, { blocked: false });
                  setLead({ ...lead, status: undefined, blockInfo: { ...lead.blockInfo, unblockedAt: new Date().toISOString() } });
                  toast.success('Lead desbloqueado.');
                } catch (err: unknown) {
                  toast.error(errorMessage(err, 'Falha ao desbloquear.'));
                }
              }}
            >
              <ShieldCheck className="size-4" /> Desbloquear
            </Button> : null}
          </div>
        </Card>
      ) : null}

      {/* Perfil */}
      <Card className="mb-6 p-5">
        <div className="flex flex-wrap items-start gap-4">
          <LeadAvatar lead={lead} className="size-20" fallbackClassName="text-xl" />
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold">{lead.profile?.name || 'Sem nome'}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <WalletPlatformBadge lead={lead} />
              <InstallStatusBadge installed={installed} />
              {genderLabel(lead.gender) ? (
                <Badge variant="secondary" className="font-normal">
                  {genderLabel(lead.gender)}
                </Badge>
              ) : null}
              {lead.lastCardResendAt ? (
                <Badge variant="outline" className="font-normal">
                  Reenviado em {formatDate(lead.lastCardResendAt)}
                </Badge>
              ) : null}
              {lead.createdAt ? (
                <Badge variant="secondary" className="font-normal">
                  Cadastro {formatDate(lead.createdAt)}
                </Badge>
              ) : null}
            </div>
            {hasMultiStore && storeNames.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {storeNames.map((name) => (
                  <Badge key={name} variant="outline" className="font-normal">
                    {name}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="mb-6 grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Qtde de compras" value={decimal(purchaseCount)} icon={ShoppingBag} index={0} loading={logsLoading} />
        <KpiCard label={`Saldo atual (${labelQtde})`} value={points} icon={Wallet} index={1} />
        <KpiCard label="Total de resgates" value={decimal(redemptionCount)} icon={Gift} index={2} loading={logsLoading} />
        <KpiCard label="Total gasto (12m)" value={currency(yearSpent, moneyCode)} icon={TrendingUp} index={3} loading={logsLoading} />
        <KpiCard label="Total gasto (vida)" value={currency(lifetimeSpent, moneyCode)} icon={Banknote} index={4} loading={logsLoading} />
        <KpiCard label="Ticket médio" value={currency(avgTicket, moneyCode)} icon={Receipt} index={5} loading={logsLoading} />
      </div>

      {/* Tabs — mobile: select full-width; desktop: segmented */}
      <div
        ref={tabsRef}
        className={cn(
          'sticky z-20 -mx-4 mb-6 bg-background/95 px-4 py-2 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80',
          'lg:-mx-8 lg:px-8',
          isInspecting ? 'top-16' : 'top-14 lg:top-0',
        )}
      >
        <div className="sm:hidden">
          <Select value={activeTab} onValueChange={(v) => handleTabChange(v as Tab)}>
            <SelectTrigger
              className="h-11 w-full rounded-xl px-4 text-sm font-medium"
              aria-label="Selecionar aba"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[60]">
              {visibleTabs.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className={cn(
                    opt.value === 'excluir' && 'text-destructive focus:text-destructive',
                  )}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <SegmentedControl
          value={activeTab}
          onChange={handleTabChange}
          options={visibleTabs}
          className="hidden w-full max-w-full sm:inline-flex"
        />
      </div>

      {/* Tab: Informações */}
      {activeTab === 'info' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="p-6">
            <div className="mb-5 rounded-2xl border border-border bg-muted/30 p-5 text-center">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{labelQtde}</p>
              <p className="mt-1 text-4xl font-bold tabular-nums">{points}</p>
            </div>
            <form onSubmit={onSubmitInfo} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor="dp-name" className="flex h-5 items-center gap-1.5 leading-none">
                    Nome
                    <Lock className="size-3 text-muted-foreground" />
                  </Label>
                  <CopyableField value={lead.profile?.name ?? ''} label="nome">
                    <Input
                      id="dp-name"
                      className="h-10"
                      value={lead.profile?.name ?? ''}
                      readOnly
                      disabled
                    />
                  </CopyableField>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="dp-uid" className="flex h-5 items-center gap-1.5 leading-none">
                    {passbook.fieldUniqueIdLabel ?? 'CPF'}
                    <Lock className="size-3 text-muted-foreground" />
                  </Label>
                  <Controller
                    name="uniqueId"
                    control={form.control}
                    render={({ field }) => (
                      <CopyableField
                        value={field.value ?? ''}
                        label={passbook.fieldUniqueIdLabel ?? 'CPF'}
                      >
                        {isCpfUniqueIdLabel(passbook.fieldUniqueIdLabel) ? (
                          <MaskedInput
                            id="dp-uid"
                            disabled
                            readOnly
                            value={field.value ?? ''}
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            mask={maskCpf}
                            inputMode="numeric"
                            placeholder="000.000.000-00"
                            className="h-10 w-full"
                          />
                        ) : (
                          <Input
                            id="dp-uid"
                            className="h-10 w-full"
                            value={field.value ?? ''}
                            readOnly
                            disabled
                          />
                        )}
                      </CopyableField>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="dp-gender" className="h-5 leading-none">
                    Gênero
                  </Label>
                  <CopyableField
                    value={genderLabel(form.watch('gender')) ?? ''}
                    label="gênero"
                  >
                    <Select
                      value={form.watch('gender') || ''}
                      onValueChange={(v) => form.setValue('gender', v, { shouldDirty: true })}
                    >
                      <SelectTrigger id="dp-gender" className="h-10 w-full">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Masculino</SelectItem>
                        <SelectItem value="F">Feminino</SelectItem>
                      </SelectContent>
                    </Select>
                  </CopyableField>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="dp-email" className="h-5 leading-none">
                    E-mail
                  </Label>
                  <CopyableField value={form.watch('email') ?? ''} label="e-mail">
                    <Input id="dp-email" type="email" className="h-10" {...form.register('email')} />
                  </CopyableField>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="dp-phone" className="h-5 leading-none">
                    Telefone
                  </Label>
                  <Controller
                    name="phone"
                    control={form.control}
                    render={({ field }) => (
                      <CopyableField value={field.value ?? ''} label="telefone">
                        <MaskedInput
                          id="dp-phone"
                          value={field.value ?? ''}
                          onValueChange={field.onChange}
                          onBlur={field.onBlur}
                          mask={maskPhone}
                          inputMode="tel"
                          placeholder="(11) 99999-9999"
                          className="h-10 w-full"
                        />
                      </CopyableField>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor="dp-birthday" className="flex h-5 items-center gap-1.5 leading-none">
                    Aniversário
                    <Lock className="size-3 text-muted-foreground" />
                  </Label>
                  <Controller
                    name="birthday"
                    control={form.control}
                    render={({ field }) => (
                      <CopyableField value={field.value ?? ''} label="aniversário" className="sm:max-w-xs">
                        <MaskedInput
                          id="dp-birthday"
                          disabled
                          readOnly
                          value={field.value ?? ''}
                          onValueChange={field.onChange}
                          onBlur={field.onBlur}
                          mask={maskBirthday}
                          inputMode="numeric"
                          placeholder="DD/MM/AAAA"
                          className="h-10 w-full"
                        />
                      </CopyableField>
                    )}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="submit" loading={form.formState.isSubmitting}>
                  Salvar cadastro
                </Button>
              </div>
            </form>

            {lead.fields && lead.fields.length > 0 && passbook.showAddFields !== false ? (
              <div className="mt-6 space-y-3 border-t border-border/50 pt-5">
                <h3 className="text-sm font-medium">Campos customizados</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {lead.fields.map((field) => {
                    const fromPassbook = passbook.addFields?.find((f) => f.slug === field.slug);
                    const label =
                      fromPassbook?.label ||
                      (field.slug === 'ondeConheceu'
                        ? 'Onde nos conheceu?'
                        : field.slug === 'nomeResponsavel'
                          ? 'Nome Responsável'
                          : field.slug === 'cpfResponsavel'
                            ? 'CPF Responsável'
                            : field.slug === 'state'
                              ? 'Estado'
                              : field.slug === 'city'
                                ? 'Cidade'
                                : field.slug ?? 'Campo');
                    return (
                      <div key={field._id ?? field.slug} className="flex flex-col gap-1.5">
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <CopyableField value={field.value ?? ''} label={label}>
                          <Input value={field.value ?? ''} readOnly disabled className="h-10 bg-muted/40" />
                        </CopyableField>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </Card>

          <Card className="p-6">
            {idPassbook ? (
              <LeadObservationsPanel
                idPassbook={idPassbook}
                lead={lead}
                onObservacaoSaved={(observacao) => setLead((prev) => (prev ? { ...prev, observacao } : prev))}
              />
            ) : null}
          </Card>
        </div>
      )}

      {/* Tab: Movimentações */}
      {activeTab === 'movimentos' && (
        <MovimentacoesTab lead={lead} labelQtde={labelQtde} passbook={passbook} />
      )}

      {/* Tab: Enviar Convite */}
      {activeTab === 'convite' && (
        <Card className="space-y-4 p-6">
          <p className="text-sm text-muted-foreground">
            Envie o cartão digital do lead por e-mail ou WhatsApp.
          </p>

          {!resendEligibility.allowed ? (
            <p className="text-sm text-warning">
              Reenvio disponível em {formatCooldownRemaining(resendEligibility.remainingMs)}.
            </p>
          ) : null}

          {lead.serialNumber && lead.email ? (
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                variant="secondary"
                disabled={sending || !resendEligibility.allowed}
                onClick={handleResendEmail}
              >
                <Mail className="size-4" />
                {sending ? 'Enviando…' : 'Reenviar por E-mail'}
              </Button>
              {lead.phone && waText ? (
                <Button variant="secondary" asChild disabled={!resendEligibility.allowed}>
                  <a
                    href={whatsappLink(lead.phone, true, waText)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageCircle className="size-4" /> Enviar pelo WhatsApp
                  </a>
                </Button>
              ) : lead.phone && !lead.hashRegenerate ? (
                <p className="text-sm text-muted-foreground self-center">
                  Reenvie o e-mail primeiro para gerar o link do WhatsApp.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Lead sem e-mail ou cartão digital gerado — não é possível enviar.
            </p>
          )}

          {lead.hashRegenerate ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Link do cartão digital</p>
              <p className="break-all rounded-lg bg-muted/40 px-3 py-2 font-mono text-xs">
                {inviteCardUrl(lead.hashRegenerate)}
              </p>
            </div>
          ) : null}

          {waText ? (
            <div className="min-w-0 space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Mensagem template (WhatsApp)
              </p>
              <p className="break-all rounded-lg bg-muted/40 px-3 py-2 text-sm [overflow-wrap:anywhere]">
                {waText}
              </p>
            </div>
          ) : null}
        </Card>
      )}

      {/* Tab: Campanhas (motor de fidelidade) */}
      {activeTab === 'campanhas' && idPassbook ? (
        <LeadLoyaltyTab idPassbook={idPassbook} uniqueId={lead.profile?.uniqueId} />
      ) : null}

      {/* Tab: Mensagens */}
      {activeTab === 'mensagens' && idPassbook ? (
        <LeadMessagesTab idPassbook={idPassbook} lead={lead} />
      ) : null}

      {/* Tab: Indicações (Indique e Ganhe) */}
      {activeTab === 'indicacoes' && idPassbook && leadId ? (
        <LeadMgmTab idPassbook={idPassbook} leadId={leadId} />
      ) : null}

      {/* Tab: Excluir */}
      {activeTab === 'excluir' && (
        <Card className="space-y-4 border-destructive/30 p-6">
          <p className="font-medium text-destructive">Zona de perigo</p>
          <p className="text-sm text-muted-foreground">
            Ao excluir o cadastro, todos os dados do lead serão removidos permanentemente e o
            cartão digital será invalidado. Esta ação não pode ser desfeita.
          </p>
          <Button variant="destructive" onClick={handleDeleteLead}>
            <Trash2 className="size-4" /> Excluir cadastro
          </Button>
        </Card>
      )}
    </>
  );
}
